<script lang="ts" setup>
import VContentBox from '@shared/component/VContentBox/VContentBox.vue';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import { computed, toRefs } from 'vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import FieldRowGroup from '@shared/form/FieldRowV2/FieldRowGroup.vue';
import { useMaatschappijenGetMaatschappijenByCcsNummers } from '@services/producten-public/orval-api/maatschappijen-api/maatschappijen-api';
import VDataTable from '@shared/component/VDataTable/VDataTable.vue';
import VColumn from '@shared/component/VDataTable/VColumn.vue';
import VHeading from '@shared/typography/VHeading/VHeading.vue';
import { type AanvragenPolissenGetPakketPolissenByRelatieSubPolisDto } from '@services/polis-public/orval-api/model/aanvragenPolissenGetPakketPolissenByRelatieSubPolisDto';
import { SUREbusinessCommonDomainSoortVerzekeringEnum as SoortVerzekering } from '@services/polis-public/orval-api/model/sUREbusinessCommonDomainSoortVerzekeringEnum';
import { currencyFormatter } from '@tools/helpers';
import { usePolisDetails } from '../usePolisDetails';

const props = defineProps<{
  polisnummer: string;
}>();

const { polisnummer } = toRefs(props);
const { data } = usePolisDetails(polisnummer);

const subpolis = computed(() =>
  data?.value?.polis?.subpolis?.length
    ? data.value.polis.subpolis.find(
        (sp: AanvragenPolissenGetPakketPolissenByRelatieSubPolisDto) =>
          sp.polisnummer === polisnummer.value,
      )
    : undefined,
);

const premiepromillages = computed(() => data?.value?.premiepromillages ?? undefined);
const premiePromillageFormatter = new Intl.NumberFormat('nl-NL', {
  minimumFractionDigits: 5,
  maximumFractionDigits: 5,
});

const verzekering = computed(() =>
  subpolis.value ? subpolis.value[data.value.polis.soortverzekering.toLowerCase()][0] : undefined,
);

const isBeursPolis = computed(() => polisnummer.value.substring(0, 3) === 'SBB');
const prolongatiepremie = computed(() => subpolis.value?.prolongatiepremie);

const { data: maatschappijenData } = useMaatschappijenGetMaatschappijenByCcsNummers({
  ccsnummers: verzekering.value?.dekking?.flatMap((dekking: any) => {
    if (
      isBeursPolis.value &&
      dekking.mijverdelingdekking &&
      dekking.mijverdelingdekking.length > 0
    ) {
      return dekking.mijverdelingdekking?.map((mij: any) => mij.maatschappijnummer);
    }
    return [];
  }),
});

function provisie(dekking: any) {
  if (Array.isArray(dekking.mijverdelingdekking)) {
    return dekking.mijverdelingdekking[0].doorlprovisieperc;
  }
  return dekking.doorlprovisieperc;
}
</script>
<template>
  <div>
    <VInfoBox type="info" v-if="data.polis.soortverzekering === SoortVerzekering.PP">
      Voor een pakketpolis zijn geen dekkingen beschikbaar.
    </VInfoBox>
    <div v-else class="mb-6 flex flex-col gap-4">
      <VContentBox
        :title="dekking.omsdekkingscode"
        v-for="dekking in verzekering.dekking"
        :key="dekking.dekkingskenmerk"
      >
        <FieldRowGroup :gap="2">
          <VHeading class="block border-b border-grey-200" :level="1" :appearance="'m'"
            >Dekking gegevens
          </VHeading>
          <FieldRowV2
            v-if="data.polis.soortverzekering === SoortVerzekering.TR"
            label="Dekkingsgebied"
          >
            {{ dekking.omsdekkingsgebiedcode }}
          </FieldRowV2>
          <FieldRowV2 label="Verzekerd bedrag">
            {{ currencyFormatter(dekking.verzekerdbedrag) }}
          </FieldRowV2>
          <FieldRowV2
            v-if="data.polis.soortverzekering !== SoortVerzekering.RB"
            label="Eigen risico"
          >
            {{ currencyFormatter(dekking.eigenrisico) }}
          </FieldRowV2>
          <FieldRowV2
            v-if="
              isBeursPolis &&
              premiepromillages[dekking.dekkingskenmerk] &&
              data.polis.soortverzekering === SoortVerzekering.BR
            "
            label="Premie promillage"
          >
            {{ premiePromillageFormatter.format(premiepromillages[dekking.dekkingskenmerk]) }}‰
          </FieldRowV2>
          <VHeading class="block border-b border-grey-200" :level="1" :appearance="'m'"
            >Premie gegevens
          </VHeading>
          <FieldRowV2
            tooltip="Let op: Dit bedrag is exclusief eventuele pakketkorting en assurantiebelasting"
            label="Netto premie"
          >
            {{ currencyFormatter(dekking.nettopremie) }}
          </FieldRowV2>
          <FieldRowV2 label="Provisie percentage"> {{ provisie(dekking) }}%</FieldRowV2>
          <FieldRowV2 label="Provisie bedrag">
            {{ currencyFormatter(dekking.doorlprovisietp) }}
          </FieldRowV2>
          <FieldRowV2 label="Ondertekenaars" v-if="isBeursPolis">
            <VDataTable
              :borderless="true"
              :sort-field="'verdeelpercentage'"
              :sort-order="-1"
              :value="dekking.mijverdelingdekking"
            >
              <VColumn header="Verdeling" field="verdeelpercentage">
                <template #body="{ data }"> {{ data.verdeelpercentage }}%</template>
              </VColumn>
              <VColumn header="Maatschappij" field="maatschappijnummer">
                <template #body="{ data }">
                  {{
                    maatschappijenData?.result.find(
                      (m) => m.maatschappijCcsnummer === data.maatschappijnummer,
                    )?.maatschappijNaam || 'Onbekend'
                  }}
                </template>
              </VColumn>
            </VDataTable>
          </FieldRowV2>
        </FieldRowGroup>
      </VContentBox>
    </div>
    <FieldRowGroup :gap="2">
      <FieldRowV2 label="Te betalen">
        {{ currencyFormatter(prolongatiepremie) }}
      </FieldRowV2>
    </FieldRowGroup>
  </div>
</template>
